import { useQuery } from '@apollo/client'
import { SortByType } from '../../constants'
import {
  TestsFull,
  TestsFullVariables,
  SortDirectionEnum,
  TestResult,
  TestType,
  Lab,
} from '../../types'
import { TESTS_FULL } from './operations'

interface TestsFilter {
  hasResult?: boolean
  isProvisioned?: boolean
  result?: TestResult
  secondResult?: TestResult
  memberLike?: string
  userIds?: string[]
  location?: string
  companyOrGroupNameLike?: string
  testedBetween?: Array<string | null>
  isParticipant?: boolean
  groupIds?: string[]
  companyIds?: string[]
  barcode?: string
  resultVerified?: boolean
  resultVerifiedAt?: string
  resultManuallyEntered?: boolean
  lab?: Lab
  type?: TestType
}

export type Sorter = {
  sortBy?: string
  direction?: SortDirectionEnum
}

interface Props {
  filter?: TestsFilter
  sorter?: Sorter
  page?: number
  pageLength?: number
  skip?: boolean
}

const PAGE_LENGTH = 10

export const useTestsData = ({
  filter,
  page = 1,
  pageLength = PAGE_LENGTH,
  skip = false,
  sorter,
}: Props) => {
  const {
    hasResult,
    isProvisioned,
    resultVerified,
    resultVerifiedAt,
    resultManuallyEntered,
    result,
    secondResult,
    userIds,
    memberLike,
    location,
    companyOrGroupNameLike,
    testedBetween,
    isParticipant,
    groupIds,
    companyIds,
    barcode,
    lab,
    type,
  } = filter || {}
  const testSessionsQuery = useQuery<TestsFull, TestsFullVariables>(TESTS_FULL, {
    variables: {
      filterData: {
        hasResult,
        isProvisioned,
        resultVerified,
        resultVerifiedAt,
        resultManuallyEntered,
        user:
          memberLike || isParticipant !== undefined
            ? { emailOrNameLike: memberLike, isParticipant }
            : null,
        userIds,
        result,
        secondResult,
        location,
        companyOrGroupNameLike,
        testedBetween,
        groupIds,
        companyIds,
        barcode,
        lab,
        type,
      },
      groupsInputData: {
        memberUserIds: userIds,
      },
      companyInputData: {
        memberUserIds: userIds,
      },
      pagination: {
        from: pageLength * (page - 1),
        length: pageLength,
        sortBy: sorter?.sortBy || SortByType.RESULT_RECIEVED_AT,
        direction: sorter?.direction || SortDirectionEnum.ASC,
      },
    },
    skip,
  })

  return testSessionsQuery
}

export const useUnverifiedManualEntryTestsData = ({
  filter,
  page = 1,
  pageLength = PAGE_LENGTH,
  skip = false,
  sorter,
}: Props) => {
  const {
    hasResult,
    isProvisioned,
    resultVerifiedAt,
    result,
    secondResult,
    userIds,
    memberLike,
    location,
    companyOrGroupNameLike,
    testedBetween,
    isParticipant,
    groupIds,
    companyIds,
    barcode,
    lab,
    type,
  } = filter || {}
  const testSessionsQuery = useQuery<TestsFull, TestsFullVariables>(TESTS_FULL, {
    variables: {
      filterData: {
        hasResult,
        isProvisioned,
        resultVerified: false,
        resultVerifiedAt,
        resultManuallyEntered: true,
        user:
          memberLike || isParticipant !== undefined
            ? { emailOrNameLike: memberLike, isParticipant }
            : null,
        userIds,
        result,
        secondResult,
        location,
        companyOrGroupNameLike,
        testedBetween,
        groupIds,
        companyIds,
        barcode,
        lab,
        type,
      },
      groupsInputData: {
        memberUserIds: userIds,
      },
      companyInputData: {
        memberUserIds: userIds,
      },
      pagination: {
        from: pageLength * (page - 1),
        length: pageLength,
        sortBy: sorter?.sortBy || SortByType.RESULT_RECIEVED_AT,
        direction: sorter?.direction || SortDirectionEnum.ASC,
      },
    },
    skip,
  })

  return testSessionsQuery
}
